import { Injectable, NgZone } from '@angular/core';
import {
  Firestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  collectionData,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Observable, from, throwError, of } from 'rxjs';
import { tap, catchError, map, switchMap } from 'rxjs/operators';
import { UserPreferencesService } from './user-preferences.service';

export interface ClickUpTask {
  taskId: string;
  jobCode: string;
  projectName: string;
  taskName: string;
  status: string;
  progress: number;
  spaceId: string;
  spaceName: string;
  folderId: string;
  folderName: string;
  listId: string;
  listName: string;
  creator: string;
  createdDate: string;
  updatedDate: string;
  archived: boolean;
  // Add any other fields as needed from portal-data
  [key: string]: any; // For any additional dynamic fields
}

@Injectable({
  providedIn: 'root',
})
export class FirestoreService {
  constructor(
    private firestore: Firestore,
    private auth: Auth,
    private ngZone: NgZone,
    private userPreferences: UserPreferencesService
  ) {}

  getTasksByUser(userEmail: string): Observable<any[]> {
    try {
      // @ts-ignore
      const appConfig = this.firestore.app.options;
    } catch (e) {
      console.error('[DEBUG] Unable to log Firebase App config:', e);
    }

    if (!this.auth.currentUser) {
      console.error('No authenticated user found');
      return throwError(() => new Error('User not authenticated'));
    }

    // Use real-time listener to auto-refresh on data updates
    const tasksRef = collection(this.firestore, 'portal-data');
    return collectionData(tasksRef, { idField: 'taskId' }).pipe(
      switchMap(async (tasks) => {
        // Apply year filtering
        return await this.filterByEnabledYears(tasks);
      }),
      tap((tasks) => {
        console.log(
          '[FirestoreService] Tasks after year filter:',
          tasks.length
        );
      }),
      catchError((err) => {
        console.error('[DEBUG] Firestore error:', err);
        return of([]);
      })
    );
  }

  /**
   * Filter data by enabled years based on user preferences
   * This is client-side filtering since Firestore doesn't support OR queries efficiently
   */
  private async filterByEnabledYears(data: any[]): Promise<any[]> {
    try {
      const enabledYears = await this.userPreferences.getEnabledYears();
      console.log(
        '[FirestoreService] Filtering by enabled years:',
        enabledYears
      );

      if (!enabledYears || enabledYears.length === 0) {
        // No filter set, return all data
        return data;
      }

      // Filter data based on creation date primarily
      const filtered = data.filter((item) => {
        // Prioritize creation date fields
        const creationDateFields = ['date_created', 'dateCreated'];

        // First check creation date
        for (const field of creationDateFields) {
          if (item[field]) {
            const year = this.extractYear(item[field]);
            if (year) {
              // If we found a creation year, use it to determine inclusion
              return enabledYears.includes(year);
            }
          }
        }

        // If no creation date found, check other date fields as fallback
        const otherDateFields = [
          'date_updated',
          'dateUpdated',
          'start_date',
          'startDate',
          'due_date',
          'dueDate',
          'date_closed',
          'dateClosed',
        ];

        for (const field of otherDateFields) {
          if (item[field]) {
            const year = this.extractYear(item[field]);
            if (year) {
              return enabledYears.includes(year);
            }
          }
        }

        // If no date fields found at all, exclude the item
        console.warn('[FirestoreService] Item has no date fields:', item);
        return false;
      });

      console.log(
        `[FirestoreService] Filtered ${data.length} items to ${filtered.length} items`
      );
      return filtered;
    } catch (error) {
      console.error('[FirestoreService] Error filtering by years:', error);
      // On error, return all data
      return data;
    }
  }

  /**
   * Extract year from various date formats
   */
  private extractYear(date: any): number | null {
    try {
      let dateObj: Date;

      // Handle Firestore Timestamp
      if (date && date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      }
      // Handle Date object
      else if (date instanceof Date) {
        dateObj = date;
      }
      // Handle string or number
      else if (typeof date === 'string' || typeof date === 'number') {
        dateObj = new Date(date);
      } else {
        return null;
      }

      if (isNaN(dateObj.getTime())) {
        return null;
      }

      return dateObj.getFullYear();
    } catch (error) {
      return null;
    }
  }
}
